import {
  UNIVERSAL_ROUTER_ABI,
  UNIVERSAL_ROUTER_BYTECODE,
} from "@gotts.ai/chain";
import { zeroAddress } from "viem";

import { deployContract } from "./deploy-utils";

import type { DeployContext } from "./deploy-utils";
import type {
  DeploymentPhaseResult,
  FoundationAddresses,
  UniversalRouterAddresses,
  V2Addresses,
  V3Addresses,
  V4Addresses,
} from "./types";
import type { Hex } from "viem";

/**
 * V3 pool init code hash from @uniswap/v3-core v1.0.1.
 *
 * This is the keccak256 of the UniswapV3Pool creation bytecode, used by
 * the UniversalRouter (and other contracts) to compute pool addresses
 * deterministically via CREATE2. Since our devenv deploys from the same
 * npm package bytecode, this constant matches.
 */
const V3_POOL_INIT_CODE_HASH =
  "0xe34f199b19b2b4f47f68442619d555527d244f78a3297ea89325f843f87b8b54";

/**
 * Phase 5: Deploy Universal Router.
 *
 * The Universal Router aggregates V2, V3, V4, and Permit2 calls into a
 * single multicall transaction. Its constructor takes a `RouterParameters`
 * struct containing addresses from all prior deployment phases.
 *
 * @param ctx - Deploy context
 * @param foundation - Phase 1 addresses (WETH9, Permit2)
 * @param v2 - Phase 2 addresses (V2 factory, init code pair hash)
 * @param v3 - Phase 3 addresses (V3 factory, NFT position manager)
 * @param v4 - Phase 4 addresses (PoolManager, PositionManager)
 */
export async function deployUniversalRouter(
  ctx: DeployContext,
  foundation: FoundationAddresses,
  v2: V2Addresses,
  v3: V3Addresses,
  v4: V4Addresses,
): Promise<DeploymentPhaseResult<UniversalRouterAddresses>> {
  const start = Date.now();

  // RouterParameters struct — matches the order in RouterParameters.sol
  const routerParams = {
    permit2: foundation.permit2,
    weth9: foundation.weth9,
    v2Factory: v2.factory,
    v3Factory: v3.factory,
    pairInitCodeHash: v2.initCodePairHash,
    poolInitCodeHash: V3_POOL_INIT_CODE_HASH,
    v4PoolManager: v4.poolManager,
    v3NFTPositionManager: v3.nonfungiblePositionManager,
    v4PositionManager: v4.positionManager,
    spokePool: zeroAddress,
  };

  const result = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: UNIVERSAL_ROUTER_ABI,
      bytecode: UNIVERSAL_ROUTER_BYTECODE as Hex,
    },
    [routerParams],
  );

  return {
    phase: 5,
    name: "Universal Router",
    addresses: { router: result.address },
    gasUsed: result.gasUsed,
    durationMs: Date.now() - start,
    txHashes: [result.txHash],
  };
}
